/**
 * Tab dispatcher for the top-level Client Plans / Goals Inventory nav.
 *
 * `switchToTab` flips the active class on the chosen `.nav-tab` and
 * `.panel` elements, then runs side-effects per panel:
 * - "clients" → reset selection + builder state, render the client list
 *   (read once, then revalidated in the background when stale — OMON-035
 *   staleness guard as reworked by OMON-351)
 * - "catalogue" → call renderCatalogue
 *
 * `bindNavTabs` wires the click handlers on every `.nav-tab` element
 * once at module init. Called from main.ts.
 *
 * Extracted from main.ts as part of OMON-060 Phase 6d.
 */

import { state } from "./state";
import {
  ensureCatalogueLive,
  ensureFreshPatients,
  isPatientListFresh,
  isPatientListLoaded,
  reloadPatients,
} from "./loaders/ensure";
import { coalesceToFrame } from "./loaders/live-maps";
import { renderClientsView } from "./clients/view";
import { refreshPatientListBody } from "./clients/list";
import { renderCatalogue } from "./catalogue/tree";
import { renderConnectOverview, renderConnectLoading } from "./connect/overview";
import { filterToIotEocs } from "./connect/sensors";
import { bootstrapProfile } from "./connect/profile";
import { canSeeConnect, canSeeSomaAdoption } from "./admin/claims";
import { go } from "./router";

/**
 * OMON-202: the panel-agnostic part of entering a top-level panel — flip the
 * active classes on the rail tab + panel, clear the admin highlight, and tear
 * down the catalogue listeners when leaving the catalogue. Split out of
 * switchToTab so the router can activate the clients panel for a client-detail
 * route WITHOUT switchToTab's clients-branch side effect (resetting the
 * selected patient back to the list).
 */
export function activateNav(panelName: string) {
  document.querySelectorAll(".nav-tab").forEach((t) => t.classList.remove("active"));
  document.querySelectorAll(".panel").forEach((p) => p.classList.remove("active"));
  document.querySelector(`[data-panel="${panelName}"]`)?.classList.add("active");
  document.getElementById(`panel-${panelName}`)?.classList.add("active");

  // OMON-137: leaving any admin panel for a regular tab clears the
  // admin-menu active highlight and drops state.adminPanel back to null.
  state.adminPanel = null;
  document.querySelectorAll("#adminMenu .admin-menu-item.active").forEach((el) =>
    el.classList.remove("active"),
  );

  // OMON-102 (revised 2026-09-24): the catalogue live-listeners are NOT torn
  // down on leaving the tab any more. Tearing down meant every re-entry
  // re-downloaded all ~690 documents; they now live for the session and are
  // detached at sign-out (resetEnsured → stopCatalogueLive). While away, a
  // remote change just updates the Maps; the render below is panel-gated.
}

/**
 * One Goals Inventory render per animation frame for listener-driven
 * changes (four listeners used to cost four full tree rebuilds on entry).
 * Skipped while another panel is showing — the tab renders on entry — and
 * during a drag-to-recategorise (OMON-166 Part 3: a mid-drag repaint would
 * yank the dragged node from under SortableJS; the drop handler renders once
 * its write lands).
 */
const scheduleLiveCatalogueRender = coalesceToFrame(() => {
  if (state.catalogueDragging) return;
  if (!document.getElementById("panel-catalogue")?.classList.contains("active")) return;
  renderCatalogue();
});

/**
 * OMON-351: re-read the client list behind an already-rendered one, then
 * repaint only the list BODY — never the toolbar, so a search the planner has
 * started typing survives — and only if the list is still what is on screen.
 * A failure leaves the cached list up (it was current a minute ago).
 */
function revalidateClientList(): void {
  reloadPatients()
    .then(() => {
      const onList = document.getElementById("panel-clients")?.classList.contains("active")
        && !state.selectedPatient
        && state.builderMode === "view";
      if (onList) refreshPatientListBody();
    })
    .catch((err) => console.warn("client list revalidate failed:", err));
}

export async function switchToTab(panelName: string) {
  activateNav(panelName);

  if (panelName === "dashboard") {
    const { enterDashboard } = await import("./dashboard/view");
    await enterDashboard(state.authClaims);
  }
  if (panelName === "clients") {
    // Clicking the SOMA Plans tab returns to the list, regardless of whether
    // we were viewing a patient's detail or building a plan.
    state.selectedPatient = null;
    state.builderMode = "view";
    state.builderGoals = [];
    // OMON-035: refresh `state.patients` from Firestore before rendering. The
    // in-memory array can drift out of sync with reality after a publish or
    // discard somewhere else in the session — without this reload, the
    // returning-to-list view sometimes showed stale plan-status / version
    // pills until the next hard refresh.
    //
    // OMON-351: that drift is now fixed at the source (every plan load
    // rewrites its client's row — see loaders/ensure.ts), so a list already
    // in hand renders AT ONCE, and is re-read behind it only when older than
    // PATIENT_LIST_FRESH_MS. It used to re-read every client (~290), and make the
    // planner wait for it, on every click of this tab.
    if (isPatientListLoaded()) {
      renderClientsView();
      if (!isPatientListFresh()) revalidateClientList();
    } else {
      await reloadPatients();
      renderClientsView();
    }
  }
  if (panelName === "catalogue") {
    // OMON-096: clicking the Goals Inventory tab returns to the list,
    // regardless of whether we were viewing or editing a goal detail.
    // Replaces the deleted "Back to Goals Inventory" button.
    state.catalogueSelectedGoal = null;
    state.catalogueEditMode = false;
    state.editingDetail = null;

    // OMON-281 + OMON-102: the catalogue is not fetched at boot — this panel
    // needs it, so the wait belongs here. `ensureCatalogueLive` attaches the
    // live listeners ONCE per session (kept after leaving the tab, torn down
    // at sign-out) so other librarians' edits appear without a refresh. If the
    // catalogue was never loaded, the listeners' first snapshots ARE the load;
    // if it was (plan prefetch / builder), they only apply changes. Either way
    // the ~690 documents are fetched once, not once per visit plus a
    // one-shot load on top.
    await ensureCatalogueLive(scheduleLiveCatalogueRender);

    renderCatalogue();
  }
  if (panelName === "chase") {
    // MONO-111 phase 2b: same population as Connect (admin/planner); same
    // deep-link bounce for anyone else. The API enforces the role again
    // server-side — this is the visibility gate only.
    if (!canSeeConnect(state.authClaims)) {
      go({ kind: "panel", panel: "dashboard" });
      return;
    }
    const { enterChaseQueue } = await import("./chase/queue");
    await enterChaseQueue();
  }
  if (panelName === "adoption") {
    // Soma planner adoption: admin-only. Deep-link bounce for anyone else;
    // the API refuses non-admins again server-side.
    if (!canSeeSomaAdoption(state.authClaims)) {
      go({ kind: "panel", panel: "dashboard" });
      return;
    }
    const { enterAdoption } = await import("./adoption/view");
    await enterAdoption();
  }
  if (panelName === "connect") {
    // BOFF-029 slice 1: IoT coordinator view — admin AND planner since OMON-234
    // (canSeeConnect; this said "admin-only" until 2026-09-28). Route guard —
    // a non-admin reaching #/connect (deep link / stale tab) is bounced to
    // the dashboard rather than shown an empty/denied panel. The tab itself
    // is hidden for non-admins in admin/shell.ts; this guards direct nav.
    if (!canSeeConnect(state.authClaims)) {
      go({ kind: "panel", panel: "dashboard" });
      return;
    }
    // Reuse the scope-aware patient loader (admin sees all; a future scoped
    // coordinator role narrows automatically), then narrow to IoT EoCs — those
    // resolving to a populated observation.device[] (OMON-209 Phase A). Only
    // sensored clients belong in the coordinator list; a non-IoT EoC slipping
    // in was the "Pilot19 — no registered devices" dev report. The IoT sweep
    // reads per EoC, so show a placeholder while it runs.
    // OMON-281: paint the placeholder BEFORE the first await, not after. It
    // used to sit behind `loadPatients()`, so on a high-latency connection the
    // tap produced no visible response at all for tens of seconds and read as
    // an unresponsive app.
    renderConnectLoading();
    // OMON-351: the SAME list the Plans tab keeps, re-read only when older
    // than PATIENT_LIST_FRESH_MS (it used to be re-read on every entry).
    // Awaited here, not revalidated behind — the IoT sweep runs over it.
    await ensureFreshPatients();
    // OMON-212: load the user's saved sensor filter (idempotent ensure-exists)
    // alongside the IoT sweep, so it's ready when a swimlane opens.
    const [, iotPatients] = await Promise.all([
      bootstrapProfile(),
      filterToIotEocs(state.patients),
    ]);
    renderConnectOverview(iotPatients);
  }
}

export function bindNavTabs() {
  document.querySelectorAll(".nav-tab").forEach((tab) => {
    tab.addEventListener("click", () => {
      // OMON-202: navigate via the URL hash; the router's hashchange listener
      // applies it (so the browser back button works). The rail nav-tabs only
      // ever carry the four workflow panels.
      const panel = (tab as HTMLElement).dataset.panel as
        "dashboard" | "clients" | "catalogue" | "connect";
      go({ kind: "panel", panel });
    });
  });
}
